import { normalizeText } from './fingerprint';

/**
 * Generates a human-readable description of a DOM element
 * Uses priority-based approach to create meaningful descriptions
 */
export function generateDescription(element: Element): string {
  const maxLength = 500;

  // Priority 1: ARIA label (highest specificity)
  const ariaLabel = element.getAttribute('aria-label');
  if (ariaLabel?.trim()) {
    return truncate(ariaLabel.trim(), maxLength);
  }

  // Priority 2: Button/Link with text
  if (isButton(element)) {
    const text = normalizeText(element.textContent || '');
    if (text) {
      return truncate(`The '${text}' button`, maxLength);
    }
    return truncate('A button', maxLength);
  }

  if (element.tagName === 'A') {
    const text = normalizeText(element.textContent || '');
    if (text) {
      return truncate(`The '${text}' link`, maxLength);
    }
    const href = element.getAttribute('href');
    if (href) {
      return truncate(`Link to '${href}'`, maxLength);
    }
    return truncate('A link', maxLength);
  }

  // Priority 3: Headings
  if (/^H[1-6]$/.test(element.tagName)) {
    const text = normalizeText(element.textContent || '');
    if (text) {
      return truncate(`The heading '${text}'`, maxLength);
    }
    return truncate(`A ${element.tagName.toLowerCase()}`, maxLength);
  }

  // Priority 4: Images
  if (element.tagName === 'IMG') {
    const alt = element.getAttribute('alt');
    if (alt) {
      return truncate(`Image: '${alt}'`, maxLength);
    }
    const src = element.getAttribute('src');
    if (src) {
      return truncate(`Image: '${src}'`, maxLength);
    }
    return truncate('An image', maxLength);
  }

  // Priority 5: Form fields with labels
  if (isFormField(element)) {
    const label = getAssociatedLabel(element);
    const fieldType = element.tagName.toLowerCase();

    if (label) {
      return truncate(`The '${label}' ${fieldType}`, maxLength);
    }

    // Fallback to placeholder for inputs
    if (element.tagName === 'INPUT') {
      const placeholder = element.getAttribute('placeholder');
      if (placeholder) {
        return truncate(`The '${placeholder}' input`, maxLength);
      }
    }

    return truncate(`A ${fieldType}`, maxLength);
  }

  // Priority 6: Fallback - tag name with parent context
  const parentContext = getParentHeading(element);
  const tagName = element.tagName.toLowerCase();

  if (parentContext) {
    return truncate(`A ${tagName} in the '${parentContext}' section`, maxLength);
  }

  return truncate(`A ${tagName}`, maxLength);
}

/**
 * Checks if element is a button (button tag or role="button")
 */
function isButton(element: Element): boolean {
  return element.tagName === 'BUTTON' || element.getAttribute('role') === 'button';
}

/**
 * Checks if element is a form field
 */
function isFormField(element: Element): boolean {
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName);
}

/**
 * Gets associated label text for a form field
 */
function getAssociatedLabel(element: Element): string | null {
  const id = element.getAttribute('id');

  // Try to find label with for attribute
  if (id) {
    const label = document.querySelector(`label[for="${id}"]`);
    if (label) {
      const text = normalizeText(label.textContent || '');
      if (text) {
        return text;
      }
    }
  }

  // Try to find parent label
  let current = element.parentElement;
  while (current) {
    if (current.tagName === 'LABEL') {
      const text = normalizeText(current.textContent || '');
      if (text) {
        return text;
      }
    }
    current = current.parentElement;
  }

  return null;
}

/**
 * Gets the nearest parent heading text for context
 */
function getParentHeading(element: Element): string | null {
  let current = element.parentElement;

  while (current && current !== document.body) {
    // Check if current element is a heading
    if (/^H[1-6]$/.test(current.tagName)) {
      const text = normalizeText(current.textContent || '');
      if (text) {
        return text;
      }
    }

    // Check if current element contains a heading
    const heading = current.querySelector('h1, h2, h3, h4, h5, h6');
    if (heading) {
      const text = normalizeText(heading.textContent || '');
      if (text) {
        return text;
      }
    }

    current = current.parentElement;
  }

  return null;
}

/**
 * Truncates text to maximum length
 */
function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength);
}
